'use client'

import { authSocialButtonClassName } from '@/components/pages/auth/libs/auth-form-styles'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import Google from '@/images/logos/google.svg'
import { SignInCodeError } from '@/lib/auth/kratos-code-login'
import { SOCIAL_PROVIDERS, type SocialProvider, startSocialSignIn } from '@/lib/auth/kratos-social-login'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { type ReactNode, useState } from 'react'

export const PROVIDER_NAMES: Record<SocialProvider, string> = {
  google: 'Google',
  apple: 'Apple',
  microsoft: 'Microsoft',
}

const AppleLogo = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="size-5 fill-current">
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
  </svg>
)

const MicrosoftLogo = () => (
  <svg viewBox="0 0 21 21" aria-hidden="true" className="size-5">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
)

const PROVIDER_LOGOS: Record<SocialProvider, ReactNode> = {
  google: <Image src={Google} alt="" aria-hidden className="size-5" />,
  apple: <AppleLogo />,
  microsoft: <MicrosoftLogo />,
}

type SocialLoginButtonsProps = {
  /** Same-origin path to land on after sign-in (forwarded to the Kratos callback as `next`). */
  returnTo?: string
  /** Disables the buttons while the surrounding form is busy. */
  disabled?: boolean
}

/** Large "Continue with …" buttons for Kratos social sign-in; signs up unknown users on the spot. */
export function SocialLoginButtons({ returnTo, disabled }: SocialLoginButtonsProps) {
  const t = useTranslations('auth.social')
  const [busy, setBusy] = useState<SocialProvider | null>(null)
  const [error, setError] = useState('')

  const signInWith = async (provider: SocialProvider) => {
    setBusy(provider)
    setError('')
    try {
      await startSocialSignIn(provider, returnTo) // navigates away on success
    } catch (err) {
      const reason = err instanceof SignInCodeError && err.reason === 'rejected' ? 'rejected' : 'unavailable'
      setError(t(reason, { provider: PROVIDER_NAMES[provider] }))
      setBusy(null)
    }
  }

  return (
    <div className="space-y-3">
      {error && (
        <p
          className="rounded-md border border-red-500/40 bg-red-950/40 px-4 py-3 text-sm text-red-200"
          role="alert"
        >
          {error}
        </p>
      )}
      {SOCIAL_PROVIDERS.map((provider) => (
        <Button
          key={provider}
          type="button"
          variant="outline"
          className={authSocialButtonClassName}
          disabled={disabled || busy !== null}
          aria-busy={busy === provider}
          onClick={() => void signInWith(provider)}
        >
          {busy === provider ? <Spinner className="size-5" /> : PROVIDER_LOGOS[provider]}
          {t(provider)}
        </Button>
      ))}
    </div>
  )
}
